import React, { useState, useEffect, useMemo } from 'react';
import { Steps, Card, Radio, Input, InputNumber, Table, Button, message, DatePicker, Checkbox, Alert, Typography, Space, Select, Row, Col, Tag, Divider, Statistic, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, UserOutlined, FileExcelOutlined, TeamOutlined, TrophyOutlined, MailOutlined, PhoneOutlined, DollarOutlined, CheckCircleFilled, WarningFilled, ContactsOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../../../services/api';
import ExcelUploader from './ExcelUploader';

const { Text, Title } = Typography;
const PRIMARY = '#128C7E';
const CREDIT_COST_PER_CHARGE = 1; // fetch from settings ideally, hardcode ok pra v1

export default function NewChargeBatchWizard({ communityTeamId, onCreated }) {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [source, setSource] = useState('manual');
  const [recipients, setRecipients] = useState([]);
  const [valueMode, setValueMode] = useState('same'); // 'same' | 'individual'
  const [sameAmount, setSameAmount] = useState(50);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dueAt, setDueAt] = useState(null);
  const [methods, setMethods] = useState({ pix: true, credit_card: true });
  const [sendMode, setSendMode] = useState('now'); // 'now' | 'schedule'
  const [sendAt, setSendAt] = useState(null);
  const [creating, setCreating] = useState(false);
  const [players, setPlayers] = useState([]);
  const [creditBalance, setCreditBalance] = useState(null);
  const [buyingCredits, setBuyingCredits] = useState(false);
  // Toggle "embutir taxa MP": cobra +taxa do pagador pra dono receber
  // exatamente o valor original líquido.
  const [absorbFee, setAbsorbFee] = useState(false);

  // Taxas MP — só o percentual do gateway. Nossa margem (Futezap) já vem via
  // créditos consumidos por cobrança, então NÃO cobramos fixo extra aqui.
  // Valores hardcoded na v1; futuro passa pra platform_settings pra configurável.
  const MP_FEE = {
    pix: { pct: 0.99, fixed: 0 },        // Pix: ~0,99% (típico MP)
    credit_card: { pct: 4.99, fixed: 0 }, // Cartão à vista: ~4,99% (típico MP)
  };
  // Prioriza PIX se aceito — é o método mais comum (>80% dos pagamentos)
  // e evita cobrar do jogador a taxa alta do cartão quando ele vai pagar via
  // PIX. Trade-off: se pagador escolher cartão, time recebe menos (aviso
  // exibido na UI).
  const pickFee = (methods) => {
    if (methods?.pix) return MP_FEE.pix;
    return MP_FEE.credit_card;
  };
  const estimateFee = (amount, methods) => {
    const f = pickFee(methods);
    return (Number(amount) * f.pct / 100) + f.fixed;
  };
  // Se absorbFee, aumenta o valor cobrado pra líquido bater com o original.
  // Fórmula: cobrado = (netAmount + fixed) / (1 - pct/100)
  const grossUp = (netAmount, methods) => {
    const f = pickFee(methods);
    return (Number(netAmount) + f.fixed) / (1 - f.pct / 100);
  };

  // Fontes novas (T5)
  const [teamInfo, setTeamInfo] = useState({ has_futezap_pro: false });
  const [rosterMembers, setRosterMembers] = useState([]);
  const [games, setGames] = useState([]);
  const [gamePlayers, setGamePlayers] = useState({}); // gameId -> players
  const [selectedGameId, setSelectedGameId] = useState(null);
  // Fontes M11 P2.5: Contatos + Grupo
  const [contacts, setContacts] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  // Ids DESMARCADOS por lista — default = todos marcados. Permite user
  // remover jogadores individualmente das listas importadas sem apagar
  // a lista completa. Chaveado por source.
  const [excludedIds, setExcludedIds] = useState({ roster: new Set(), players: new Set(), game: new Set() });

  // Carrega players Futezap se source='players' — usa endpoint da comunidade
  // (o /organizer-players do admin exige req.user.customer_id que não existe aqui).
  useEffect(() => {
    if (source === 'players' && players.length === 0 && communityTeamId) {
      api.get('/community/finance/charges/sources/team-players', { params: { team_id: communityTeamId } })
        .then(r => setPlayers(r.data.items || []))
        .catch((e) => {
          const msg = e.response?.status === 404
            ? 'Este time não tem Futezap Pro ativo — use outra fonte'
            : (e.response?.data?.message || 'Falhou carregar jogadores');
          message.warning(msg);
        });
    }
  }, [source, communityTeamId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ao trocar fonte, limpa destinatários e seleções — impede batch.source
  // ficar dessincronizado dos recipients (bug pego no review final M5).
  useEffect(() => {
    setRecipients([]);
    setSelectedGameId(null);
    setSelectedContactIds([]);
    setSelectedGroupId(null);
    setExcludedIds({ roster: new Set(), players: new Set(), game: new Set() });
  }, [source]);

  // Info do time (detecta Futezap Pro pra mostrar/esconder fontes)
  useEffect(() => {
    if (!communityTeamId) return;
    api.get('/community/finance/charges/sources/team-info', { params: { team_id: communityTeamId } })
      .then(r => setTeamInfo(r.data)).catch(() => {});
  }, [communityTeamId]);

  // Status da conta MP — necessária pra receber pagamento. Se não conectada,
  // bloqueia criação (backend também bloqueia com 412; aqui é warn UX).
  const [mpConnected, setMpConnected] = useState(null); // null=loading, bool=known
  useEffect(() => {
    if (!communityTeamId) return;
    api.get('/community/finance/mp-connection/status', { params: { team_id: communityTeamId } })
      .then(r => setMpConnected(!!r.data.connected))
      .catch(() => setMpConnected(false));
  }, [communityTeamId]);

  // Carrega roster da comunidade sob demanda
  useEffect(() => {
    if (source !== 'roster' || !communityTeamId) return;
    api.get('/community/finance/charges/sources/team-roster', { params: { team_id: communityTeamId } })
      .then(r => setRosterMembers(r.data.items || []))
      .catch(() => {});
  }, [source, communityTeamId]);

  // Recalcula recipients do roster excluindo os desmarcados
  useEffect(() => {
    if (source !== 'roster') return;
    setRecipients(rosterMembers
      .filter(m => !excludedIds.roster.has(m.cp_id))
      .map(m => ({
        name: m.name, phone: m.phone, email: m.email, amount_brl: sameAmount,
        source_type: 'community_players', source_ref_id: m.cp_id,
      })));
  }, [source, rosterMembers, excludedIds.roster]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalcula recipients do Futezap Pro excluindo os desmarcados
  useEffect(() => {
    if (source !== 'players' || players.length === 0) return;
    setRecipients(players
      .filter(p => !excludedIds.players.has(p.id))
      .map(p => ({
        name: p.name, phone: p.phone, email: p.email || '', amount_brl: sameAmount,
        source_type: 'players', source_ref_id: p.id,
      })));
  }, [source, players, excludedIds.players]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carrega jogos (Futezap Pro) sob demanda
  useEffect(() => {
    if (source !== 'game' || !communityTeamId || !teamInfo.has_futezap_pro) return;
    api.get('/community/finance/charges/sources/team-games', { params: { team_id: communityTeamId } })
      .then(r => setGames(r.data.items || [])).catch(() => {});
  }, [source, communityTeamId, teamInfo.has_futezap_pro]);

  // Carrega contatos e grupos sob demanda (sempre disponível — não precisa Pro)
  useEffect(() => {
    if ((source !== 'contacts' && source !== 'group') || !communityTeamId) return;
    api.get('/community/finance/contacts', { params: { team_id: communityTeamId } })
      .then(r => setContacts(r.data.items || [])).catch(() => {});
    api.get('/community/finance/contact-groups', { params: { team_id: communityTeamId } })
      .then(r => setContactGroups(r.data.items || [])).catch(() => {});
  }, [source, communityTeamId]);

  // Ao selecionar contatos, monta recipients
  useEffect(() => {
    if (source !== 'contacts') return;
    const map = new Map(contacts.map(c => [c.id, c]));
    setRecipients(selectedContactIds.map(id => {
      const c = map.get(id);
      if (!c) return null;
      return {
        name: c.name, phone: c.phone || '', email: c.email || '',
        amount_brl: sameAmount,
        source_type: 'contact', source_ref_id: c.id,
      };
    }).filter(Boolean));
  }, [source, selectedContactIds, contacts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ao selecionar grupo, carrega membros e importa todos
  useEffect(() => {
    if (source !== 'group' || !selectedGroupId || !communityTeamId) return;
    api.get('/community/finance/contacts', {
      params: { team_id: communityTeamId, group_id: selectedGroupId },
    })
      .then(r => {
        const items = r.data.items || [];
        setRecipients(items.map(c => ({
          name: c.name, phone: c.phone || '', email: c.email || '',
          amount_brl: sameAmount,
          source_type: 'contact', source_ref_id: c.id,
        })));
      })
      .catch(() => setRecipients([]));
  }, [source, selectedGroupId, communityTeamId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ao escolher jogo, carrega players (recipients calculado no useEffect abaixo)
  const loadGamePlayers = async (gameId) => {
    setSelectedGameId(gameId);
    setExcludedIds(prev => ({ ...prev, game: new Set() }));
    try {
      const r = await api.get(`/community/finance/charges/sources/team-game-players/${gameId}`,
        { params: { team_id: communityTeamId } });
      setGamePlayers(prev => ({ ...prev, [gameId]: r.data.items || [] }));
    } catch { setGamePlayers(prev => ({ ...prev, [gameId]: [] })); }
  };

  // Recalcula recipients dos players do jogo excluindo os desmarcados
  useEffect(() => {
    if (source !== 'game' || !selectedGameId) return;
    const list = gamePlayers[selectedGameId] || [];
    setRecipients(list
      .filter(p => !excludedIds.game.has(p.player_id))
      .map(p => ({
        name: p.name, phone: p.phone, email: p.email || '', amount_brl: sameAmount,
        source_type: 'players', source_ref_id: p.player_id,
      })));
  }, [source, selectedGameId, gamePlayers, excludedIds.game]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sempre que valueMode ou sameAmount muda, aplica valor
  useEffect(() => {
    if (valueMode === 'same') {
      setRecipients(prev => prev.map(r => ({ ...r, amount_brl: sameAmount })));
    }
  }, [valueMode, sameAmount]);

  // Carrega saldo de créditos
  useEffect(() => {
    if (!communityTeamId) return;
    api.get('/community/finance/credits/status', { params: { team_id: communityTeamId } })
      .then(r => setCreditBalance(r.data.balance))
      .catch(() => {});
  }, [communityTeamId]);

  // Refetch saldo ao voltar da aba de checkout MP
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && communityTeamId) {
        api.get('/community/finance/credits/status', { params: { team_id: communityTeamId } })
          .then(r => setCreditBalance(r.data.balance)).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [communityTeamId]);

  const finalRecipients = useMemo(() => recipients.filter(r => r.name && (r.email || r.phone)), [recipients]);
  const creditsNeeded = finalRecipients.length * CREDIT_COST_PER_CHARGE;

  const addManual = () => setRecipients(r => [...r, { name: '', phone: '', email: '', amount_brl: sameAmount }]);
  const editRecipient = (idx, field, value) => setRecipients(r => r.map((x, i) => i === idx ? { ...x, [field]: value } : x));
  const removeRecipient = idx => setRecipients(r => r.filter((_, i) => i !== idx));
  const importPlayers = () => setRecipients(players.map(p => ({ name: p.name, phone: p.phone, email: p.email || '', amount_brl: sameAmount, source_type: 'players', source_ref_id: p.id })));

  const importRoster = () => setRecipients(rosterMembers.map(m => ({
    name: m.name, phone: m.phone, email: m.email, amount_brl: sameAmount,
    source_type: 'community_players', source_ref_id: m.cp_id,
  })));

  const importGamePlayers = () => {
    const list = gamePlayers[selectedGameId] || [];
    setRecipients(list.map(p => ({
      name: p.name, phone: p.phone, email: p.email || '', amount_brl: sameAmount,
      source_type: 'players', source_ref_id: p.player_id,
    })));
  };

  const buyCreditsInline = async () => {
    setBuyingCredits(true);
    try {
      // pack B (100 créditos) — cobre a maioria dos lotes
      const r = await api.post('/community/finance/credits/purchase', {
        community_team_id: communityTeamId, pack_key: 'b',
      });
      if (r.data.checkout_url) {
        window.open(r.data.checkout_url, '_blank');
        message.info('Abrindo o Mercado Pago em outra aba. Volta aqui depois de pagar e clica em Criar de novo.');
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 503) message.error('Compra indisponível agora. Fala com o suporte.');
      else message.error(e.response?.data?.message || 'Falhou abrir a compra');
    } finally { setBuyingCredits(false); }
  };

  const create = async () => {
    if (!name.trim()) return message.warning('Dá um nome pro lote');
    if (finalRecipients.length === 0) return message.warning('Sem destinatários válidos');
    if (sendMode === 'schedule' && !sendAt) {
      return message.warning('Escolhe data e hora do agendamento');
    }
    if (!methods.pix && !methods.credit_card) {
      return message.warning('Escolhe pelo menos um método de pagamento (Pix ou Cartão)');
    }
    setCreating(true);
    try {
      const body = {
        community_team_id: communityTeamId,
        name: name.trim(),
        description,
        due_at: dueAt ? dueAt.toISOString() : null,
        send_at: sendMode === 'schedule' && sendAt ? sendAt.toISOString() : null,
        methods,
        source,
        recipients: finalRecipients.map(r => ({
          name: r.name, phone: r.phone || null, email: r.email || null,
          // Se embutir taxa: cobra do pagador o valor bruto pra dono receber
          // o líquido escolhido. Arredonda pra 2 casas.
          amount_brl: absorbFee
            ? Math.round(grossUp(r.amount_brl, methods) * 100) / 100
            : Number(r.amount_brl),
          source_type: r.source_type, source_ref_id: r.source_ref_id,
        })),
      };
      const r = await api.post('/community/finance/charges/batch', body);
      message.success(`${finalRecipients.length} cobranças criadas`);
      onCreated?.(r.data);
      nav(`/comunidade/financeiro/lote/${r.data.batch_id}`);
    } catch (e) {
      if (e.response?.status === 409) message.error(e.response.data.message || 'Créditos insuficientes');
      else if (e.response?.status === 412 && e.response?.data?.code === 'MP_NOT_CONNECTED') {
        message.error(e.response.data.message);
        setMpConnected(false);
      }
      else message.error(e.response?.data?.message || 'Falhou criar cobrança');
    } finally { setCreating(false); }
  };

  const steps = [
    { title: 'Destinatários' },
    { title: 'Valor' },
    { title: 'Detalhes' },
    { title: 'Confirmação' },
  ];

  const SOURCE_META = {
    contacts: { icon: <ContactsOutlined />, label: 'Contatos', hint: 'Escolhe da sua agenda salva' },
    group: { icon: <UsergroupAddOutlined />, label: 'Grupo de contatos', hint: 'Ex: Mensalistas, Convidados' },
    roster: { icon: <TeamOutlined />, label: 'Meus jogadores da comunidade', hint: 'Importa quem já tá no time' },
    manual: { icon: <EditOutlined />, label: 'Digitar um por um', hint: 'Cadastro manual dos jogadores' },
    excel: { icon: <FileExcelOutlined />, label: 'Subir planilha', hint: 'Excel ou CSV com nome/email/telefone/valor' },
    players: { icon: <UserOutlined />, label: 'Jogadores do Futezap Pro', hint: 'Importa do sistema Pro do time' },
    game: { icon: <TrophyOutlined />, label: 'Quem jogou uma partida específica', hint: 'Presença numa das últimas 8 partidas' },
  };
  const availableSources = ['contacts', 'group', 'roster', 'manual', 'excel', ...(teamInfo.has_futezap_pro ? ['players', 'game'] : [])];

  const nextDisabled = step === 0 && finalRecipients.length === 0;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 4px' }}>
      <Row justify="space-between" align="middle" wrap gutter={[8, 8]} style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>Nova cobrança</Title>
          <Text type="secondary">Passo {step + 1} de 4</Text>
        </Col>
        <Col>
          <Button onClick={() => nav('/comunidade/financeiro')}>Sair do wizard</Button>
        </Col>
      </Row>

      {mpConnected === false && (
        <Alert
          type="warning"
          showIcon
          message="Conta Mercado Pago não conectada"
          description="Sem conectar a sua conta MP você não recebe os pagamentos. Conecte antes de criar cobrança."
          action={
            <Button type="primary" danger size="small"
              onClick={() => nav('/comunidade/financeiro/configuracoes')}>
              Conectar agora
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <Alert
        type="info"
        showIcon
        message="Isso é uma mensalidade que se repete todo mês?"
        description={
          <span>
            Se sim, use <b>Recorrência</b> — o sistema gera automático todo dia X. Cobrança avulsa (esse wizard) é pra caso único.{' '}
            <a onClick={() => nav('/comunidade/financeiro/recorrencias')}>Ir pra recorrências</a>
          </span>
        }
        closable
        style={{ marginBottom: 12 }}
      />

      <Card styles={{ body: { padding: 20 } }}>
        <Steps current={step} items={steps} style={{ marginBottom: 28 }} responsive />

        {step === 0 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>De onde vêm os destinatários?</Title>
              <Row gutter={[12, 12]}>
                {availableSources.map(k => {
                  const meta = SOURCE_META[k];
                  const active = source === k;
                  return (
                    <Col xs={24} sm={12} md={availableSources.length > 3 ? 8 : 12} key={k}>
                      <div
                        onClick={() => setSource(k)}
                        style={{
                          border: `2px solid ${active ? PRIMARY : '#e6e6e6'}`,
                          background: active ? '#f0faf7' : '#fff',
                          borderRadius: 10,
                          padding: 16,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          minHeight: 88,
                          display: 'flex', flexDirection: 'column', gap: 4,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: active ? PRIMARY : '#666', fontSize: 18 }}>{meta.icon}</span>
                          <strong style={{ color: active ? PRIMARY : '#333' }}>{meta.label}</strong>
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>{meta.hint}</Text>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>

            <Divider style={{ margin: 0 }} />

            {source === 'manual' && (
              <div>
                <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                  <Title level={5} style={{ margin: 0 }}>Destinatários ({recipients.length})</Title>
                  <Button onClick={addManual} icon={<PlusOutlined />} type="primary" style={{ background: PRIMARY, borderColor: PRIMARY }}>
                    Adicionar
                  </Button>
                </Row>
                {recipients.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', background: '#fafafa', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                    <Text type="secondary">Nenhum destinatário ainda. Clica em "Adicionar" pra começar.</Text>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <Table rowKey={(_, i) => i} size="middle"
                      columns={[
                        { title: 'Nome', dataIndex: 'name', render: (v, _r, i) => <Input value={v} onChange={e => editRecipient(i, 'name', e.target.value)} placeholder="Nome completo" /> },
                        { title: 'Email', dataIndex: 'email', render: (v, _r, i) => <Input value={v} onChange={e => editRecipient(i, 'email', e.target.value)} placeholder="email@..." prefix={<MailOutlined style={{ color: '#bbb' }} />} /> },
                        { title: 'Telefone', dataIndex: 'phone', render: (v, _r, i) => <Input value={v} onChange={e => editRecipient(i, 'phone', e.target.value)} placeholder="5511..." prefix={<PhoneOutlined style={{ color: '#bbb' }} />} /> },
                        { title: '', width: 50, render: (_v, _r, i) => <Button danger type="text" icon={<DeleteOutlined />} onClick={() => removeRecipient(i)} /> },
                      ]}
                      dataSource={recipients} pagination={false} scroll={{ x: 600 }} />
                  </div>
                )}
              </div>
            )}

            {source === 'roster' && (
              <div>
                <Alert
                  type="success" showIcon
                  message={`${finalRecipients.length} de ${rosterMembers.length} jogadores selecionados`}
                  description="Todos vêm marcados. Desmarque quem não quer cobrar."
                  style={{ marginBottom: 12 }}
                />
                {rosterMembers.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', background: '#fafafa', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                    <Text type="secondary">Nenhum jogador ativo no seu time comunidade ainda.</Text>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <Table rowKey="cp_id" size="middle" dataSource={rosterMembers} pagination={false}
                      rowSelection={{
                        selectedRowKeys: rosterMembers
                          .filter(m => !excludedIds.roster.has(m.cp_id))
                          .map(m => m.cp_id),
                        onChange: (keys) => {
                          const selected = new Set(keys);
                          const excluded = new Set(rosterMembers.filter(m => !selected.has(m.cp_id)).map(m => m.cp_id));
                          setExcludedIds(prev => ({ ...prev, roster: excluded }));
                        },
                      }}
                      columns={[
                        { title: 'Nome', dataIndex: 'name' },
                        { title: 'Email', dataIndex: 'email', render: v => v || <Text type="secondary">—</Text> },
                        { title: 'Telefone', dataIndex: 'phone', render: v => v || <Text type="secondary">—</Text> },
                      ]}
                      scroll={{ x: 500 }} />
                  </div>
                )}
              </div>
            )}

            {source === 'excel' && (
              <ExcelUploader defaultAmount={sameAmount}
                onParsed={list => setRecipients(list.map(r => ({
                  ...r, source_type: 'anon', source_ref_id: null,
                })))} />
            )}

            {source === 'contacts' && (
              <div>
                {contacts.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', background: '#fafafa', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                    <Text type="secondary">
                      Nenhum contato salvo ainda. Cria um em <strong>Financeiro → Contatos</strong> primeiro,
                      ou use outra fonte — contatos são criados automaticamente ao cobrar.
                    </Text>
                  </div>
                ) : (
                  <>
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                      Escolhe os contatos ({selectedContactIds.length} selecionados):
                    </Text>
                    <Select
                      mode="multiple"
                      style={{ width: '100%' }}
                      placeholder="Busca por nome, email ou telefone…"
                      value={selectedContactIds}
                      onChange={setSelectedContactIds}
                      optionFilterProp="label"
                      maxTagCount="responsive"
                      allowClear
                      showSearch
                      options={contacts.map(c => ({
                        value: c.id,
                        label: `${c.name}${c.email ? ' · ' + c.email : ''}${c.phone ? ' · ' + c.phone : ''}`,
                      }))}
                    />
                    <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button size="small" onClick={() => setSelectedContactIds(contacts.map(c => c.id))}>
                        Selecionar todos ({contacts.length})
                      </Button>
                      <Button size="small" onClick={() => setSelectedContactIds([])} disabled={selectedContactIds.length === 0}>
                        Limpar seleção
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {source === 'group' && (
              <div>
                {contactGroups.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', background: '#fafafa', borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                    <Text type="secondary">
                      Nenhum grupo ainda. Cria um em <strong>Financeiro → Contatos → Novo grupo</strong>.
                    </Text>
                  </div>
                ) : (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: 6 }}>Escolhe o grupo:</Text>
                      <Select
                        style={{ width: '100%' }}
                        placeholder="Selecione um grupo"
                        value={selectedGroupId}
                        onChange={setSelectedGroupId}
                        options={contactGroups.map(g => ({
                          value: g.id,
                          label: `${g.name} · ${g.member_count} contato(s)`,
                        }))}
                      />
                    </div>
                    {selectedGroupId && finalRecipients.length > 0 && (
                      <>
                        <Alert type="success" showIcon
                          message={`${finalRecipients.length} contatos importados do grupo`} />
                        <div style={{ overflowX: 'auto' }}>
                          <Table rowKey="source_ref_id" size="middle" dataSource={recipients} pagination={false}
                            columns={[
                              { title: 'Nome', dataIndex: 'name' },
                              { title: 'Email', dataIndex: 'email', render: v => v || <Text type="secondary">—</Text> },
                              { title: 'Telefone', dataIndex: 'phone', render: v => v || <Text type="secondary">—</Text> },
                            ]}
                            scroll={{ x: 500 }} />
                        </div>
                      </>
                    )}
                    {selectedGroupId && finalRecipients.length === 0 && (
                      <Alert type="warning" showIcon
                        message="Esse grupo não tem contatos com email ou telefone válidos" />
                    )}
                  </Space>
                )}
              </div>
            )}

            {source === 'players' && (
              <div>
                <Alert
                  type="success" showIcon
                  message={`${finalRecipients.length} de ${players.length} jogadores selecionados`}
                  description="Todos vêm marcados. Desmarque quem não quer cobrar."
                  style={{ marginBottom: 12 }}
                />
                <div style={{ overflowX: 'auto' }}>
                  <Table rowKey="id" size="middle" dataSource={players} pagination={false}
                    rowSelection={{
                      selectedRowKeys: players
                        .filter(p => !excludedIds.players.has(p.id))
                        .map(p => p.id),
                      onChange: (keys) => {
                        const selected = new Set(keys);
                        const excluded = new Set(players.filter(p => !selected.has(p.id)).map(p => p.id));
                        setExcludedIds(prev => ({ ...prev, players: excluded }));
                      },
                    }}
                    columns={[
                      { title: 'Nome', dataIndex: 'name' },
                      { title: 'Email', dataIndex: 'email', render: v => v || <Text type="secondary">—</Text> },
                      { title: 'Telefone', dataIndex: 'phone', render: v => v || <Text type="secondary">—</Text> },
                    ]}
                    scroll={{ x: 500 }} />
                </div>
              </div>
            )}

            {source === 'game' && (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 6 }}>Escolhe a partida:</Text>
                  <Select style={{ width: '100%' }} placeholder="Selecione uma partida"
                    value={selectedGameId} onChange={loadGamePlayers}
                    options={games.map(g => ({
                      value: g.game_id,
                      label: `${g.date_of_game || 'Sem data'} · ${g.status || 'partida'}`,
                    }))} />
                </div>
                {selectedGameId && (
                  <div>
                    <Alert type="success" showIcon
                      message={`${finalRecipients.length} de ${(gamePlayers[selectedGameId] || []).length} jogadores selecionados`}
                      description="Todos vêm marcados. Desmarque quem não quer cobrar."
                      style={{ marginBottom: 12 }} />
                    <div style={{ overflowX: 'auto' }}>
                      <Table rowKey="player_id" size="middle" dataSource={gamePlayers[selectedGameId] || []} pagination={false}
                        rowSelection={{
                          selectedRowKeys: (gamePlayers[selectedGameId] || [])
                            .filter(p => !excludedIds.game.has(p.player_id))
                            .map(p => p.player_id),
                          onChange: (keys) => {
                            const selected = new Set(keys);
                            const list = gamePlayers[selectedGameId] || [];
                            const excluded = new Set(list.filter(p => !selected.has(p.player_id)).map(p => p.player_id));
                            setExcludedIds(prev => ({ ...prev, game: excluded }));
                          },
                        }}
                        columns={[
                          { title: 'Nome', dataIndex: 'name' },
                          { title: 'Telefone', dataIndex: 'phone', render: v => v || <Text type="secondary">—</Text> },
                        ]}
                        scroll={{ x: 400 }} />
                    </div>
                  </div>
                )}
              </Space>
            )}
          </Space>
        )}

        {step === 1 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>Como é o valor da cobrança?</Title>
              <Row gutter={12}>
                {[
                  { key: 'same', label: 'Mesmo valor pra todos', hint: 'Ex: mensalidade de R$50' },
                  { key: 'individual', label: 'Valor individual', hint: 'Cada jogador com seu valor' },
                ].map(opt => {
                  const active = valueMode === opt.key;
                  return (
                    <Col xs={24} sm={12} key={opt.key}>
                      <div
                        onClick={() => setValueMode(opt.key)}
                        style={{
                          border: `2px solid ${active ? PRIMARY : '#e6e6e6'}`,
                          background: active ? '#f0faf7' : '#fff',
                          borderRadius: 10, padding: 16, cursor: 'pointer', minHeight: 72,
                        }}
                      >
                        <strong style={{ color: active ? PRIMARY : '#333', display: 'block' }}>{opt.label}</strong>
                        <Text type="secondary" style={{ fontSize: 12 }}>{opt.hint}</Text>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
            {valueMode === 'same' && (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>
                  {absorbFee ? 'Valor que você quer receber (líquido):' : 'Valor de cada cobrança:'}
                </Text>
                <InputNumber
                  min={1}
                  value={sameAmount}
                  onChange={setSameAmount}
                  prefix="R$"
                  size="large"
                  decimalSeparator=","
                  precision={2}
                  style={{ width: '100%', maxWidth: 240 }}
                />
                {absorbFee ? (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                      Cobrado do jogador (bruto):{' '}
                      <Text strong style={{ color: '#faad14' }}>
                        R$ {(Math.round(grossUp(sameAmount || 0, methods) * 100) / 100).toFixed(2).replace('.', ',')}
                      </Text>
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      × {finalRecipients.length} jogador(es) selecionado(s).
                    </Text>
                  </div>
                ) : (
                  <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                    Vai cobrar R$ {Number(sameAmount || 0).toFixed(2).replace('.', ',')} de cada um dos {finalRecipients.length} jogadores
                  </Text>
                )}
              </div>
            )}
            {valueMode === 'individual' && (
              <div style={{ overflowX: 'auto' }}>
                <Table rowKey={(_, i) => i} size="middle"
                  columns={[
                    { title: 'Nome', dataIndex: 'name' },
                    { title: absorbFee ? 'Você recebe' : 'Valor', dataIndex: 'amount_brl', width: 180, render: (v, _r, i) =>
                      <InputNumber
                        min={1}
                        value={v}
                        onChange={x => editRecipient(i, 'amount_brl', x)}
                        prefix="R$"
                        decimalSeparator=","
                        precision={2}
                        style={{ width: '100%' }}
                      /> },
                    ...(absorbFee ? [{
                      title: 'Cobrado',
                      width: 130,
                      align: 'right',
                      render: (_v, r) => {
                        const gross = Math.round(grossUp(r.amount_brl || 0, methods) * 100) / 100;
                        return (
                          <Tooltip title="Valor cobrado do jogador (bruto). Inclui a taxa MP pra você receber o valor da coluna 'Você recebe'.">
                            <Text strong style={{ color: '#faad14' }}>R$ {gross.toFixed(2).replace('.', ',')}</Text>
                          </Tooltip>
                        );
                      },
                    }] : []),
                  ]}
                  dataSource={recipients} pagination={false} scroll={{ x: 500 }} />
              </div>
            )}
          </Space>
        )}

        {step === 2 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>Nome do lote *</Text>
              <Input placeholder="Ex: Mensalidade agosto, Rachão do sábado" value={name} onChange={e => setName(e.target.value)} size="large" />
              <Text type="secondary" style={{ fontSize: 12 }}>Aparece pra você no painel</Text>
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>Descrição (opcional)</Text>
              <Input.TextArea placeholder="Ex: Mensalidade referente ao mês de agosto" rows={3}
                value={description} onChange={e => setDescription(e.target.value)} maxLength={500} showCount />
              <Text type="secondary" style={{ fontSize: 12 }}>O pagador vê essa mensagem na cobrança</Text>
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>Vencimento (opcional)</Text>
                <DatePicker placeholder="Escolhe uma data" value={dueAt} onChange={setDueAt} format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Col>
              <Col xs={24} sm={12}>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>Métodos de pagamento *</Text>
                <Space>
                  <Checkbox checked={methods.pix} onChange={e => setMethods(m => ({ ...m, pix: e.target.checked }))}>Pix</Checkbox>
                  <Checkbox checked={methods.credit_card} onChange={e => setMethods(m => ({ ...m, credit_card: e.target.checked }))}>Cartão</Checkbox>
                </Space>
              </Col>
            </Row>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Quando enviar?</Text>
              <Row gutter={12}>
                {[
                  { key: 'now', label: 'Enviar agora', hint: 'Cobrança sai assim que confirmar' },
                  { key: 'schedule', label: 'Agendar', hint: 'Escolhe data e hora' },
                ].map(opt => {
                  const active = sendMode === opt.key;
                  return (
                    <Col xs={24} sm={12} key={opt.key}>
                      <div
                        onClick={() => setSendMode(opt.key)}
                        style={{
                          border: `2px solid ${active ? PRIMARY : '#e6e6e6'}`,
                          background: active ? '#f0faf7' : '#fff',
                          borderRadius: 10, padding: 12, cursor: 'pointer', minHeight: 60,
                        }}
                      >
                        <strong style={{ color: active ? PRIMARY : '#333', display: 'block' }}>{opt.label}</strong>
                        <Text type="secondary" style={{ fontSize: 12 }}>{opt.hint}</Text>
                      </div>
                    </Col>
                  );
                })}
              </Row>
              {sendMode === 'schedule' && (
                <div style={{ marginTop: 12 }}>
                  <DatePicker showTime value={sendAt} onChange={setSendAt} format="DD/MM/YYYY HH:mm" style={{ width: '100%', maxWidth: 280 }} placeholder="Data e hora do envio" />
                </div>
              )}
            </div>
          </Space>
        )}

        {step === 3 && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row gutter={[12, 12]}>
              <Col xs={12} sm={6}>
                <Card size="small" styles={{ body: { padding: 12 } }}>
                  <Statistic title="Destinatários" value={finalRecipients.length} valueStyle={{ color: PRIMARY, fontSize: 22 }} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" styles={{ body: { padding: 12 } }}>
                  <Statistic title="Valor total" prefix="R$" value={finalRecipients.reduce((s, r) => s + Number(r.amount_brl || 0), 0).toFixed(2).replace('.', ',')} valueStyle={{ fontSize: 22 }} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" styles={{ body: { padding: 12 } }}>
                  <Statistic title="Créditos p/ enviar" value={creditsNeeded} valueStyle={{ color: creditBalance !== null && creditsNeeded > creditBalance ? '#dc2626' : PRIMARY, fontSize: 22 }} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" styles={{ body: { padding: 12 } }}>
                  <Statistic title="Seu saldo" value={creditBalance ?? '—'} valueStyle={{ fontSize: 22 }} />
                </Card>
              </Col>
            </Row>

            {name && (
              <Card size="small" title="Detalhes do lote">
                <Row gutter={[8, 4]}>
                  <Col span={24}><Text type="secondary">Nome:</Text> <Text strong>{name}</Text></Col>
                  {description && <Col span={24}><Text type="secondary">Descrição:</Text> <Text>{description}</Text></Col>}
                  {dueAt && <Col xs={12}><Text type="secondary">Vencimento:</Text> <Text>{dueAt.format('DD/MM/YYYY')}</Text></Col>}
                  <Col xs={12}>
                    <Text type="secondary">Métodos:</Text>{' '}
                    {methods.pix && <Tag color="green">Pix</Tag>}
                    {methods.credit_card && <Tag color="blue">Cartão</Tag>}
                  </Col>
                  <Col span={24}>
                    <Text type="secondary">Envio:</Text>{' '}
                    <Text strong>{sendMode === 'schedule' && sendAt ? `Agendado pra ${sendAt.format('DD/MM/YYYY HH:mm')}` : 'Assim que confirmar'}</Text>
                  </Col>
                </Row>
              </Card>
            )}

            {finalRecipients.length > 0 && (() => {
              const totalGross = finalRecipients.reduce((s, r) => s + Number(r.amount_brl || 0), 0);
              const feeByMethod = (methodKey) => {
                // Retorna { gross, netTotal, feeTotal, samplePerPayer }
                const cfg = MP_FEE[methodKey];
                const perCharge = finalRecipients.map(r => {
                  const original = Number(r.amount_brl);
                  const gross = absorbFee ? (original + cfg.fixed) / (1 - cfg.pct / 100) : original;
                  const fee = gross * cfg.pct / 100 + cfg.fixed;
                  return { gross, fee, net: gross - fee, original };
                });
                return {
                  netTotal: perCharge.reduce((s, x) => s + x.net, 0),
                  grossTotal: perCharge.reduce((s, x) => s + x.gross, 0),
                  sampleNet: perCharge[0].net,
                  sampleGross: perCharge[0].gross,
                  sampleOriginal: perCharge[0].original,
                  pct: cfg.pct,
                };
              };
              const fmt = v => `R$ ${Number(v).toFixed(2).replace('.', ',')}`;
              const showPix = methods.pix;
              const showCard = methods.credit_card;
              const pixData = showPix ? feeByMethod('pix') : null;
              const cardData = showCard ? feeByMethod('credit_card') : null;
              return (
                <Card size="small" title="💰 Quanto vai entrar na sua conta MP">
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Row gutter={[12, 12]}>
                      {pixData && (
                        <Col xs={24} sm={showCard ? 12 : 24}>
                          <div style={{ textAlign: 'center', padding: 12, background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Se pagar via Pix</Text>
                            <Text strong style={{ fontSize: 24, color: PRIMARY, lineHeight: 1.2 }}>{fmt(pixData.netTotal)}</Text>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>taxa ~{pixData.pct}%</Text>
                          </div>
                        </Col>
                      )}
                      {cardData && (
                        <Col xs={24} sm={showPix ? 12 : 24}>
                          <div style={{ textAlign: 'center', padding: 12, background: '#e6f4ff', borderRadius: 8, border: '1px solid #91caff' }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Se pagar com Cartão</Text>
                            <Text strong style={{ fontSize: 24, color: '#0958d9', lineHeight: 1.2 }}>{fmt(cardData.netTotal)}</Text>
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>taxa ~{cardData.pct}%</Text>
                          </div>
                        </Col>
                      )}
                    </Row>

                    <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 6 }}>
                      <Checkbox checked={absorbFee} onChange={e => setAbsorbFee(e.target.checked)}>
                        <Text strong>Embutir a taxa no valor cobrado</Text>
                      </Checkbox>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4, paddingLeft: 24 }}>
                        {absorbFee
                          ? (methods.pix && methods.credit_card
                              ? `Cálculo assume PIX (~1%). Se o pagador escolher cartão (~5%), você recebe um pouco menos.`
                              : methods.pix
                                ? `PIX (~1%). Pagador paga R$ ${(Math.round(grossUp(sameAmount || 0, methods) * 100) / 100).toFixed(2).replace('.', ',')} pra você receber R$ ${Number(sameAmount || 0).toFixed(2).replace('.', ',')}.`
                                : `Cartão (~5%). Pagador paga R$ ${(Math.round(grossUp(sameAmount || 0, methods) * 100) / 100).toFixed(2).replace('.', ',')} pra você receber R$ ${Number(sameAmount || 0).toFixed(2).replace('.', ',')}.`
                            )
                          : `Sem marcar, você recebe o valor menos a taxa MP. Marque pra cobrar a taxa do pagador.`
                        }
                      </Text>
                    </div>

                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Estimativa. Valor real depende do seu plano no Mercado Pago.
                      Cobrado total: <strong>{fmt(showPix && pixData ? pixData.grossTotal : cardData.grossTotal)}</strong>
                      {absorbFee && showPix && showCard && (
                        <> (assumindo PIX; se cartão ~ {fmt(cardData.grossTotal)})</>
                      )}.
                    </Text>
                  </Space>
                </Card>
              );
            })()}

            <div>
              <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>Destinatários</Title>
              {finalRecipients.length === 0 ? (
                <Alert type="error" showIcon icon={<WarningFilled />}
                  message="Sem destinatários"
                  description="Volta pro passo 1 e escolhe uma fonte, ou adiciona destinatários manualmente." />
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <Table rowKey={(_, i) => i} size="small"
                    columns={[
                      { title: 'Nome', dataIndex: 'name' },
                      { title: 'Email', dataIndex: 'email', render: v => v || <Text type="secondary">—</Text> },
                      { title: 'Telefone', dataIndex: 'phone', render: v => v || <Text type="secondary">—</Text> },
                      { title: absorbFee ? 'Você recebe' : 'Valor', dataIndex: 'amount_brl', width: 110, align: 'right',
                        render: v => <Text strong>R$ {Number(v).toFixed(2).replace('.', ',')}</Text> },
                      ...(absorbFee ? [{
                        title: 'Cobrado', width: 110, align: 'right',
                        render: (_v, r) => {
                          const gross = Math.round(grossUp(r.amount_brl || 0, methods) * 100) / 100;
                          return (
                            <Tooltip title="Valor cobrado do jogador (bruto). Inclui a taxa MP pra você receber o líquido da coluna 'Você recebe'.">
                              <Text strong style={{ color: '#faad14' }}>R$ {gross.toFixed(2).replace('.', ',')}</Text>
                            </Tooltip>
                          );
                        },
                      }] : []),
                    ]}
                    dataSource={finalRecipients} pagination={{ pageSize: 10, hideOnSinglePage: true }} scroll={{ x: 500 }} />
                </div>
              )}
            </div>

            {creditBalance !== null && creditsNeeded > creditBalance && (
              <Alert type="error" showIcon
                message="Créditos insuficientes"
                description={
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>Você tem <strong>{creditBalance}</strong> créditos e precisa de <strong>{creditsNeeded}</strong>. Compra mais e já dispara a cobrança.</Text>
                    <Button type="primary" loading={buyingCredits} onClick={buyCreditsInline} icon={<DollarOutlined />} style={{ background: PRIMARY, borderColor: PRIMARY }}>
                      Comprar créditos agora
                    </Button>
                  </Space>
                }
              />
            )}
            {creditBalance !== null && creditsNeeded <= creditBalance && finalRecipients.length > 0 && (
              <Alert type="success" showIcon icon={<CheckCircleFilled />}
                message={`Tudo certo. Sobram ${creditBalance - creditsNeeded} créditos depois dessa cobrança.`}
              />
            )}
            {recipients.length > finalRecipients.length && (
              <Alert type="warning" showIcon
                message={`${recipients.length - finalRecipients.length} destinatário(s) sem email E sem telefone serão ignorados.`}
              />
            )}
          </Space>
        )}

        <Divider style={{ margin: '24px 0 16px' }} />
        <div className="charge-wizard-footer" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {step > 0 && <Button onClick={() => setStep(s => s - 1)}>Voltar</Button>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {step < 3 && (
              <Button type="primary" size="large" onClick={() => setStep(s => s + 1)}
                disabled={nextDisabled}
                style={{ background: nextDisabled ? undefined : PRIMARY, borderColor: nextDisabled ? undefined : PRIMARY }}>
                Próximo
              </Button>
            )}
            {step === 3 && (
              <Button type="primary" size="large" loading={creating} onClick={create}
                disabled={finalRecipients.length === 0 || (creditBalance !== null && creditsNeeded > creditBalance)}
                style={{ background: PRIMARY, borderColor: PRIMARY }}>
                {sendMode === 'schedule' && sendAt ? `Agendar pra ${sendAt.format('DD/MM HH:mm')}` : 'Criar e enviar cobranças'}
              </Button>
            )}
          </div>
        </div>
        <style>{`
          @media (max-width: 576px) {
            .charge-wizard-footer > div > .ant-btn { flex: 1 1 auto; }
            .charge-wizard-footer { flex-direction: column-reverse; }
            .charge-wizard-footer > div { width: 100%; display: flex; }
          }
        `}</style>
      </Card>
    </div>
  );
}
